/**
 * VariantConfigurator — custom element for products with more than 3 options.
 *
 * Reads the option_schema metafield to render N display options, then maps
 * user selections back to Shopify's 3-option variant system by computing
 * composite "Config" values from config-source display options.
 *
 * Integrates with product-info via PUB_SUB_EVENTS.optionValueSelectionChange,
 * using Shopify option value IDs for server-side variant resolution.
 */
class VariantConfigurator extends HTMLElement {
  constructor() {
    super();
    this.schema = null;
    this.optionValueIdMap = null;
  }

  connectedCallback() {
    let schemaRaw = JSON.parse(this.querySelector('[data-option-schema]').textContent);
    this.schema = typeof schemaRaw === 'string' ? JSON.parse(schemaRaw) : schemaRaw;
    this.optionValueIdMap = JSON.parse(this.querySelector('[data-option-value-ids]').textContent);
    this.fieldsets = [...this.querySelectorAll('.variant-configurator__option')];

    console.log('[VariantConfigurator] schema stored_options:', this.schema.stored_options);
    console.log('[VariantConfigurator] option value ID map:', this.optionValueIdMap);

    this.addEventListener('change', this.onOptionChange.bind(this));

    // Initial state
    this.applyVisibilityRules();

    // Liquid renders price/SKU from product.selected_or_first_available_variant,
    // which uses Shopify's native variant order. Our radio buttons render in the
    // schema's display_options order, with the first value of each option checked.
    // If those don't agree, publish the radio selection so product-info catches up.
    this.syncRenderedVariant();

    // Option image popout
    this.initImagePopout();

    // Subscribe to variant changes to show GCS SKU images
    this._gcsRequestId = 0;
    this.onVariantChangeUnsubscriber = subscribe(
      PUB_SUB_EVENTS.variantChange,
      this.handleVariantChange.bind(this)
    );
  }

  disconnectedCallback() {
    this.onVariantChangeUnsubscriber?.();
  }

  getSelectedValue(optionName) {
    const fieldset = this.fieldsets.find((f) => f.dataset.optionName === optionName);
    if (!fieldset) return null;
    const checked = fieldset.querySelector('input[type="radio"]:checked');
    return checked ? checked.value : null;
  }

  applyVisibilityRules() {
    for (const fieldset of this.fieldsets) {
      const whenOption = fieldset.dataset.visibleWhenOption;
      const whenNotEquals = fieldset.dataset.visibleWhenNotEquals;

      if (!whenOption || !whenNotEquals) continue;

      const depValue = this.getSelectedValue(whenOption);
      const notEqualsValues = whenNotEquals.split(',');
      const ruleTriggered = depValue && notEqualsValues.includes(depValue);
      const hideValuesRaw = fieldset.dataset.visibleWhenHideValues || '';
      const hideValues = hideValuesRaw ? hideValuesRaw.split(',') : [];

      if (hideValues.length === 0) {
        // No specific values — hide entire option
        fieldset.style.display = ruleTriggered ? 'none' : '';
        fieldset.dataset.hidden = ruleTriggered ? 'true' : 'false';
      } else {
        // Hide specific values only
        fieldset.style.display = '';
        fieldset.dataset.hidden = 'false';
        const radios = fieldset.querySelectorAll('input[type="radio"]');
        let hiddenSelected = false;
        for (const radio of radios) {
          const shouldHide = ruleTriggered && hideValues.includes(radio.value);
          radio.style.display = shouldHide ? 'none' : '';
          const label = fieldset.querySelector(`label[for="${radio.id}"]`);
          if (label) label.style.display = shouldHide ? 'none' : '';
          if (shouldHide && radio.checked) hiddenSelected = true;
        }
        if (hiddenSelected) {
          const firstVisible = [...radios].find((r) => r.style.display !== 'none');
          if (firstVisible) {
            firstVisible.checked = true;
            firstVisible.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }
    }
  }

  /**
   * Compute the 3 Shopify option values from current display selections.
   */
  computeShopifyOptions() {
    const delimiter = this.schema.config_delimiter || ' / ';
    const displayOptions = this.schema.display_options;
    const storedOptions = this.schema.stored_options;

    const result = [];

    for (const shopifyOptName of storedOptions) {
      const directOpt = displayOptions.find(
        (d) => (d.source === 'option1' || d.source === 'option2' || d.source === 'option3') &&
               d.name === shopifyOptName
      );

      if (directOpt) {
        result.push(this.getSelectedValue(directOpt.name));
        continue;
      }

      // Config option — build composite
      const configOpts = displayOptions
        .filter((d) => d.source === 'config')
        .sort((a, b) => a.index - b.index);

      const visibleConfigOpts = configOpts.filter((cfg) => {
        const fieldset = this.fieldsets.find(
          (f) => f.dataset.optionName === cfg.name
        );
        return !fieldset || fieldset.dataset.hidden !== 'true';
      });

      const parts = visibleConfigOpts.map((cfg) => this.getSelectedValue(cfg.name));
      result.push(parts.join(delimiter));
    }

    return result;
  }

  /**
   * Look up Shopify option value IDs for the computed option values.
   */
  lookupOptionValueIds(shopifyOptions) {
    const storedOptions = this.schema.stored_options;
    const ids = [];

    for (let i = 0; i < storedOptions.length; i++) {
      const optName = storedOptions[i];
      const value = shopifyOptions[i];
      const id = this.optionValueIdMap?.[optName]?.[value];
      if (id) ids.push(id);
    }

    return ids;
  }

  syncRenderedVariant() {
    let rendered = null;
    try {
      const raw = this.querySelector('[data-selected-variant]')?.textContent;
      if (raw) rendered = JSON.parse(raw);
    } catch (e) { /* swallow — we'll just publish below */ }

    const shopifyOptions = this.computeShopifyOptions();

    if (rendered) {
      const renderedOptions = [rendered.option1, rendered.option2, rendered.option3];
      const inSync = shopifyOptions.every((v, i) => v === renderedOptions[i]);
      if (inSync) return;
    }

    const optionValueIds = this.lookupOptionValueIds(shopifyOptions);
    if (optionValueIds.length === 0) return;

    publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
      data: {
        event: { target: this },
        target: this,
        selectedOptionValues: optionValueIds,
      },
    });
  }

  initImagePopout() {
    var overlay = document.getElementById('option-image-popout');
    console.log('[VariantConfigurator] popout overlay:', overlay);
    console.log('[VariantConfigurator] option images found:', this.querySelectorAll('.option-image').length);
    if (!overlay) return;
    var overlayImg = overlay.querySelector('img');

    this.querySelectorAll('label.has-option-image').forEach(function(label) {
      var img = label.querySelector('.option-image');
      label.addEventListener('mouseenter', function() {
        overlayImg.src = img.src;
        overlayImg.alt = img.alt;
        overlay.classList.add('is-visible');
      });
      label.addEventListener('mouseleave', function() {
        overlay.classList.remove('is-visible');
      });
    });
  }

  handleVariantChange({ data: { sectionId, html, variant } }) {
    if (sectionId !== this.dataset.section) return;

    const productInfo = this.closest('product-info');
    if (!productInfo) return;

    const mediaGallery = productInfo.querySelector('media-gallery');
    if (!mediaGallery) return;

    const galleryList = mediaGallery.querySelector('ul[id^="Slider-Gallery"]');
    if (!galleryList) return;

    if (!variant?.sku) {
      galleryList.querySelector('[data-gcs-sku-image]')?.remove();
      return;
    }

    const requestId = ++this._gcsRequestId;
    const sku = variant.sku;
    const imageUrl = `https://storage.googleapis.com/stack-light-public/products/${encodeURIComponent(sku)}.png`;
    const section = productInfo.dataset.section;

    const probe = new Image();
    probe.onload = () => {
      if (requestId !== this._gcsRequestId) return;

      galleryList.querySelector('[data-gcs-sku-image]')?.remove();

      const slide = document.createElement('li');
      slide.setAttribute('data-gcs-sku-image', '');
      slide.setAttribute('data-media-id', `${section}-gcs-sku`);
      slide.className = 'product__media-item grid__item slider__slide is-active product__media-item--gcs';

      const img = document.createElement('img');
      img.src = imageUrl;
      img.alt = variant.title || sku;
      slide.appendChild(img);

      galleryList.querySelectorAll('[data-media-id]').forEach((el) =>
        el.classList.remove('is-active')
      );
      galleryList.prepend(slide);

      const viewer = mediaGallery.querySelector('[id^="GalleryViewer"]');
      if (viewer?.resetPages) viewer.resetPages();
    };
    probe.src = imageUrl;
  }

  onOptionChange(event) {
    this.applyVisibilityRules();

    const shopifyOptions = this.computeShopifyOptions();
    const optionValueIds = this.lookupOptionValueIds(shopifyOptions);

    console.log('[VariantConfigurator] shopify options:', shopifyOptions);
    console.log('[VariantConfigurator] option value IDs:', optionValueIds);

    publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
      data: {
        event,
        target: event.target,
        selectedOptionValues: optionValueIds,
      },
    });
  }
}

customElements.define('variant-configurator', VariantConfigurator);
